import React, { useState, useEffect } from 'react';

import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import BackButton from '../../components/common/BackButton';
import TimePeriodSelector from '../../components/common/TimePeriodSelector';
import CategoryParetoChart from '../../components/technologist/CategoryParetoChart';
import TechnologistCategoryChart from '../../components/technologist/TechnologistCategoryChart';
import TechnologistComparisonChart from '../../components/technologist/TechnologistComparisonChart';
import TechnologistObservationChart from '../../components/technologist/TechnologistObservationChart';
import { ROLES } from '../../constants/general';
import { GRANULARITY_VALUES, MODALITY_OPTIONS_LABELS } from '../../constants/observations';
import TechnologistService from '../../services/technologist.service';

const technologistService = new TechnologistService();

const useStyles = makeStyles((theme) => ({
  backButton: {
    marginBottom: theme.spacing(2),
    textTransform: 'none',
  },
  chartContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    marginTop: 24,
  },
  chartTitle: {
    borderBottom: '1px solid #eaeaea',
    fontWeight: 600,
    marginBottom: '15px',
    paddingBottom: '10px',
  },
  distributonChart: {
    height: 300,
    width: '100%',
  },
  infoContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    margin: '20px 0',
    padding: '20px',
  },
  infoHR: {
    backgroundColor: '#EBEBEB',
    border: 'none',
    height: 1,
    width: '100%',
  },
  infoItem: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    justifyContent: 'flex-start',
  },
  infoLabel: {
    color: '#555',
    fontSize: 12,
    fontWeight: 'bold',
  },
  infoValue: {
    color: '#999',
    fontSize: 14,
    lineHeight: 1.37,
  },
  labelText: {
    color: '#172B4D',
    fontSize: 12,
    fontWeight: 600,
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    height: '200px',
    justifyContent: 'center',
    width: '100%',
  },
  mainTitle: {
    color: '#263238',
    fontSize: 24,
    fontWeight: 500,
  },
  pageContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    marginTop: 20,
    width: '100%',
  },
  pageHeader: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  pageTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    marginBottom: 20,
  },
  percentile: {
    fontSize: 14,
    fontWeight: 600,
  },
  periodSelector: {
    marginTop: 16,
  },
  positionText: {
    fontSize: 12,
    fontWeight: 700,
  },
  qaTitle: {
    borderBottom: '1px solid #E8ECF3',
    fontSize: 16,
    fontWeight: 700,
    padding: '14px 14px 10px',
  },
  root: {
    '& .recharts-cartesian-axis-tick-line': {
      stroke: 'transparent',
    },
    '& .recharts-cartesian-grid-horizontal line': {
      stroke: '#eaeaea',
    },
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 46,
    padding: '20px 70px',
    textAlign: 'left',
  },
  statContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 5,
    boxShadow: '0px 0px 10px 0px #0000001A',
  },
  statGrid: {
    display: 'grid',
    gridGap: 20,
    gridTemplateColumns: '200px 1fr',
    padding: '14px 14px 16px',
  },
  statGridThreeColumn: {
    display: 'grid',
    gridTemplateColumns: '200px 1fr',
    padding: '14px 14px 16px',
  },
  statValue: {
    color: '#999999',
    fontSize: 14,
    fontWeight: 700,
  },
  statValueNumber: {
    fontSize: 14,
    fontWeight: 700,
  },
  technologistName: {
    color: theme.palette.primary.main,
    fontSize: 20,
    lineHeight: 1.2,
    marginBottom: 3,
  },
  timeGranularityContainer: {
    marginLeft: theme.spacing(2),
  },
}));

const TechnologistSummaryView = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { technologistId } = useParams();

  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [timeGranularity, setTimeGranularity] = useState(GRANULARITY_VALUES.MONTHLY);
  const [chartError, setChartError] = useState(null);

  const fetchSummaryData = async () => {
    setLoading(true);
    setChartError(null);

    try {
      const response = await technologistService.getTechnologistSummary(technologistId, startDate, endDate, timeGranularity);
      setSummaryData(response);
    } catch (err) {
      console.error('Error fetching technologist summary data:', err);
      setChartError(err.message || 'Failed to load technologist summary data');
      enqueueSnackbar('Failed to load technologist summary data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryData = async () => {
    setCategoryLoading(true);
    setCategoryError(null);

    try {
      const response = await technologistService.getCategoryStatistics(technologistId, startDate, endDate);
      setCategoryData(response);
    } catch (err) {
      console.error('Error fetching category statistics:', err);
      setCategoryError(err.message || 'Failed to load category statistics');
      enqueueSnackbar('Failed to load category statistics', { variant: 'error' });
    } finally {
      setCategoryLoading(false);
    }
  };

  const fetchComparisonData = async () => {
    setComparisonLoading(true);
    setComparisonError(null);

    try {
      const response = await technologistService.getTechnologistComparison(startDate, endDate);
      setComparisonData(response);
    } catch (err) {
      console.error('Error fetching technologist comparison:', err);
      setComparisonError(err.message || 'Failed to load technologist comparison');
      enqueueSnackbar('Failed to load technologist comparison', { variant: 'error' });
    } finally {
      setComparisonLoading(false);
    }
  };

  useEffect(() => {
    fetchSummaryData();
    fetchCategoryData();
    fetchComparisonData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technologistId, startDate, endDate, timeGranularity]);

  const handleTimePeriodUpdate = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleTimeGranularityChange = (newGranularity) => {
    setTimeGranularity(newGranularity);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case ROLES.ADMIN.role:
        return 'Lead Technologist';
      case ROLES.LEAD_TECH.role:
        return 'Lead Tech';
      case ROLES.RADIOLOGIST.role:
        return 'Radiologist';
      case ROLES.TECHNOLOGIST.role:
        return 'Technologist';
      default:
        return role;
    }
  };

  const formatResponseTime = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';

    if (minutes < 60) {
      return `${Math.round(minutes)} minutes`;
    }

    const hours = minutes / 60;

    if (hours > 48) {
      return `${Math.round(hours)} hours`;
    }

    if (hours > 4) {
      return `${parseFloat(hours.toFixed(2))} hours`;
    }

    const wholeHours = Math.floor(hours);
    const remainingMinutes = Math.round(minutes % 60);

    if (remainingMinutes === 0) {
      return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'}`;
    }

    return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'} ${remainingMinutes} minutes`;
  };

  const averageResponseTime = formatResponseTime(summaryData?.statResponseTime?.technologistResponseTime);
  const generalAverageResponseTime = formatResponseTime(summaryData?.statResponseTime?.generalResponseTime);

  const InfoItem = ({ label, value }) => (
    <Box className={classes.infoItem}>
      <Typography variant="subtitle2" className={classes.infoLabel}>
        {label}:
      </Typography>
      <Typography variant="body2" className={classes.infoValue}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <div className={classes.root}>
      <Box width={690} display="flex" flexWrap="wrap" flexDirection="column">
        <Box justifyContent="flex-start">
          <BackButton route="/users" goBackHistory />

          <div className={classes.pageTitle}>
            <Typography className={classes.mainTitle}>Technologist Summary Page</Typography>
          </div>

          <Box display="flex" flexDirection="column" gridGap={8}>
            <Box display="flex" flexDirection="row" alignItems="center" gridGap={8}>
              <Box className={classes.technologistName}>{summaryData?.name}</Box>
              <Typography className={classes.positionText}>({getRoleLabel(summaryData?.role)})</Typography>
            </Box>

            <hr className={classes.infoHR} />

            <Box display="flex" flexDirection="row" gridGap={40} mb={1}>
              {summaryData?.facility && <InfoItem label="Facility" value={summaryData.facility} />}
              {summaryData?.modality && <InfoItem label="Modalities" value={MODALITY_OPTIONS_LABELS[summaryData.modality]} />}
              {summaryData?.fullTimeEmployment != null && <InfoItem label="FTE" value={summaryData.fullTimeEmployment} />}
            </Box>
          </Box>
        </Box>

        <Box className={classes.pageContent}>
          <Box className={classes.statContainer}>
            <Box className={classes.qaTitle}>QA Cases</Box>

            <Box className={classes.statGrid}>
              <Box>
                <Typography className={classes.labelText}>Number of QA cases in time period:</Typography>
                <Typography className={classes.statValue}>{summaryData?.qaCasesStats?.count}</Typography>
              </Box>
              <Box>
                <Typography className={classes.labelText}>Average QA cases across all technologists:</Typography>
                <Typography className={classes.statValue}>{summaryData?.qaCasesStats?.average}</Typography>
              </Box>
            </Box>
          </Box>

          <Box className={classes.statContainer}>
            <Box className={classes.qaTitle}>Stat QA response time</Box>

            <Box className={classes.statGrid}>
              <Box>
                <Typography className={classes.labelText}>Time to respond to STAT QA cases:</Typography>
                <Typography className={classes.statValue}>{averageResponseTime}</Typography>
              </Box>
              <Box>
                <Typography className={classes.labelText}>Average technologists response time:</Typography>
                <Typography className={classes.statValue}>{generalAverageResponseTime}</Typography>
              </Box>
            </Box>
          </Box>

          <Box className={classes.statContainer}>
            <Box className={classes.qaTitle}>Quiz scores</Box>

            <Box className={classes.statGrid}>
              <Box>
                <Typography className={classes.labelText}>Cumulative Quiz score:</Typography>
                <Typography className={classes.statValue}>
                  {summaryData?.quizScores?.technologistAverageScore != null ? `${Number(summaryData.quizScores.technologistAverageScore).toFixed(1)}%` : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography className={classes.labelText}>Total quiz scores across all technologists:</Typography>
                <Typography className={classes.statValue}>
                  {summaryData?.quizScores?.generalAverageScore != null ? `${Number(summaryData.quizScores.generalAverageScore).toFixed(1)}%` : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box display="flex" flexDirection="row" gridGap="20px">
            <Box className={classes.statContainer} flex="1">
              <Box className={classes.qaTitle}>Errors</Box>

              <Box className={classes.statGridThreeColumn}>
                <Typography className={classes.labelText}>Number of errors:</Typography>
                <Typography className={classes.statValue}>{summaryData?.errorStats?.count ?? 'N/A'}</Typography>
                <Typography className={classes.labelText}>Number of errors/ FTE:</Typography>
                <Typography className={classes.statValue}>{summaryData?.errorStats?.countPerFte ?? 'N/A'}</Typography>
                <Typography className={`${classes.labelText} ${classes.percentile}`}>Percentile:</Typography>
                <Typography className={classes.statValueNumber}>{summaryData?.errorStats?.percentile ?? 'N/A'}</Typography>
              </Box>
            </Box>

            <Box className={classes.statContainer} flex="1">
              <Box className={classes.qaTitle}>Kudos</Box>

              <Box className={classes.statGridThreeColumn}>
                <Typography className={classes.labelText}>Number of thumbs up:</Typography>
                <Typography className={classes.statValue}>{summaryData?.kudosStats?.count ?? 'N/A'}</Typography>
                <Typography className={classes.labelText}>Number of thumbs up/ FTE:</Typography>
                <Typography className={classes.statValue}>{summaryData?.kudosStats?.countPerFte ?? 'N/A'}</Typography>
                <Typography className={`${classes.labelText} ${classes.percentile}`}>Percentile:</Typography>
                <Typography className={classes.statValueNumber}>{summaryData?.kudosStats?.percentile ?? 'N/A'}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box className={classes.chartContainer} flex="1">
          {/* Technologist Comparison Chart */}
          <Box className={classes.chartContainer}>
            <TechnologistComparisonChart
              data={comparisonData}
              loading={comparisonLoading}
              error={comparisonError}
              startDate={startDate}
              endDate={endDate}
              highlightTechnologistId={parseInt(technologistId, 10)}
              hideTables
            />
          </Box>
        </Box>
      </Box>

      {/* Charts Section */}
      <Box flexDirection="column" flex={1}>
        <Box display="flex" justifyContent="flex-end" width="100%" mt={4} mb={17}>
          <TimePeriodSelector avoidWeekly onUpdate={handleTimePeriodUpdate} onGranularityChange={handleTimeGranularityChange} timeGranularity={timeGranularity} />
        </Box>

        <Box className={classes.chartContainer}>
          <TechnologistObservationChart
            data={summaryData?.observationsByDate || []}
            loading={loading}
            error={chartError}
            onRefresh={fetchSummaryData}
            startDate={startDate}
            endDate={endDate}
          />
        </Box>

        {/* Category Statistics Charts */}
        <TechnologistCategoryChart data={categoryData} loading={categoryLoading} error={categoryError} startDate={startDate} endDate={endDate} />

        <Box display="flex" flexDirection="row" gridGap="16px">
          {/* Category Pareto Chart */}
          <Box className={classes.chartContainer} flex="1">
            <CategoryParetoChart data={categoryData} loading={categoryLoading} error={categoryError} startDate={startDate} endDate={endDate} />
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default TechnologistSummaryView;
